import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Spinner from 'react-bootstrap/Spinner'
import { toast } from 'react-toastify'
import { useRunAnalytic, useConfirmAnalytic } from 'api/hooks/use-run-analytic'
import { useUpdateAnalyticConfigs } from 'api/hooks/use-analytic-configs'
import { useQueryClient } from 'react-query'

const RunAnalyticButton = ({ analyticId, configOverrides, configChanged, currentCount }) => {
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [previewData, setPreviewData] = useState(null)

  const { mutate: runAnalytic, isLoading: isRunning } = useRunAnalytic({
    options: {
      onSuccess: (data) => {
        setPreviewData(data)
      },
      onError: (error) => {
        const message = error.response?.data?.error || 'Failed to run analytic'
        toast.error(`Error: ${message}`)
      }
    }
  })

  const { mutate: updateConfigs } = useUpdateAnalyticConfigs()

  const { mutate: confirmAnalytic, isLoading: isImporting } = useConfirmAnalytic({
    options: {
      onSuccess: () => {
        if (configOverrides && Object.keys(configOverrides).length > 0) {
          updateConfigs(configOverrides, {
            onSuccess: () => queryClient.invalidateQueries(['analytic_configs'])
          })
        }
        queryClient.invalidateQueries(['analytic'])
        queryClient.invalidateQueries(['analytics'])
        // Custom analytics persist overrides into the recipe (server-side on
        // import), so refresh the config-keys metadata to show the new values.
        queryClient.invalidateQueries(['analytic_config_keys'])
        toast.success('Import started! Results will be imported in the background.')
        setShowModal(false)
        setPreviewData(null)
      },
      onError: (error) => {
        const message = error.response?.data?.error || 'Failed to start import'
        toast.error(`Error: ${message}`)
      }
    }
  })

  const handleOpen = () => {
    setPreviewData(null)
    setShowModal(true)
    runAnalytic({ analyticId, configOverrides: configOverrides || {} })
  }

  const handleClose = () => {
    setShowModal(false)
    setPreviewData(null)
  }

  const handleAcceptImport = () => {
    confirmAnalytic({ analyticId, outputKey: previewData.output_key, configOverrides })
  }

  return (
    <>
      <Button
        variant="primary"
        size="sm"
        onClick={handleOpen}
        className="mb-3"
      >
        Run Analytic
      </Button>

      <Modal show={showModal} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Run Analytic</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {configChanged
              ? 'This will run the analytic with the updated configuration.'
              : 'This will run the analytic with its current configuration.'}
          </p>
          {isRunning && (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" className="me-2" />
              Running analytics...
            </div>
          )}
          {previewData && (
            <>
              <p className="mb-0">
                {configChanged ? 'Adjusting the config value created' : 'This run created'} <strong>{(previewData.flags_count || 0).toLocaleString()}</strong> results
                compared to <strong>{(currentCount || 0).toLocaleString()}</strong> that were previously created.
              </p>
              {previewData.flags_count > 50000 && (
                <div
                  className="mt-3 px-3 py-2 rounded"
                  style={{
                    borderLeft: '4px solid var(--bs-danger)',
                    backgroundColor: 'var(--bs-danger-bg-subtle, #f8d7da)',
                    color: 'var(--bs-danger-text-emphasis, #58151c)'
                  }}
                >
                  Results exceed the 50,000 limit. Please adjust the configuration to produce fewer results before importing.
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="link" onClick={handleClose} disabled={isRunning || isImporting}>
            Cancel
          </Button>
          {previewData && (
            <Button variant="primary" onClick={handleAcceptImport} disabled={isImporting || previewData.flags_count > 50000}>
              {isImporting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Starting Import...
                </>
              ) : (
                'Accept & Import'
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  )
}

RunAnalyticButton.propTypes = {
  analyticId: PropTypes.string.isRequired,
  configOverrides: PropTypes.object,
  configChanged: PropTypes.bool,
  currentCount: PropTypes.number
}

export default RunAnalyticButton
